import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AIMarketingAgent from '../../utils/aiMarketingAgent';
import CustomerDashboard from './CustomerDashboard';
// import EmailSetupGuide from './EmailSetupGuide'; // Temporarily disabled for deployment
import { getCustomerStats } from '../../utils/customerDataManager';

const AIMarketingDashboard = ({ isOpen, onClose }) => {
  const [marketingAgent, setMarketingAgent] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dailyReport, setDailyReport] = useState(null);
  const [customerView, setCustomerView] = useState('dashboard'); // 'dashboard' or 'email-setup'

  useEffect(() => {
    if (isOpen && !marketingAgent) {
      initializeAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const initializeAgent = async () => {
    const agent = new AIMarketingAgent({
      websiteUrl: 'https://www.makranamarble.online',
      businessName: 'Makrana Marble',
      targetKeywords: [
        'makrana marble online', 'buy marble india', 'premium marble slabs',
        'temple carving marble', 'luxury marble handicrafts', 'authentic makrana'
      ]
    });

    const initialized = await agent.initialize();
    if (initialized) {
      setMarketingAgent(agent);
      setDashboardData(agent.getDashboardData());
    }
  };

  const startAutomation = async () => {
    if (marketingAgent) {
      setIsRunning(true);
      const report = await marketingAgent.startAutomation();
      setDailyReport(report);
      setDashboardData(marketingAgent.getDashboardData());
    }
  };

  const stopAutomation = () => {
    setIsRunning(false);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'customers', label: 'Customers', icon: '👥' },
    { id: 'activities', label: 'Activities', icon: '📝' },
    { id: 'automation', label: 'Automation', icon: '🤖' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'guide', label: 'Guide', icon: '📚' }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  🤖 AI Marketing Agent
                </h2>
                <p className="text-blue-100 mt-1">
                  Automated website promotion system for Makrana Marble
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isRunning ? 'bg-green-500' : 'bg-gray-500'}`}>
                  <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-200 animate-pulse' : 'bg-gray-200'}`}></div>
                  <span className="text-sm">{isRunning ? 'Running' : 'Stopped'}</span>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Control Panel */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4">🚀 Quick Actions</h3>
                  <div className="flex gap-4">
                    {!isRunning ? (
                      <button
                        onClick={startAutomation}
                        disabled={!marketingAgent}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ▶️ Start AI Promotion
                      </button>
                    ) : (
                      <button
                        onClick={stopAutomation}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        ⏹️ Stop Automation
                      </button>
                    )}
                    <button
                      onClick={() => setDashboardData(marketingAgent?.getDashboardData())}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      🔄 Refresh Data
                    </button>
                  </div>
                </div>

                {/* Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 text-white p-2 rounded-lg">📱</div>
                      <div>
                        <h4 className="font-semibold text-blue-900">Social Media</h4>
                        <p className="text-blue-700 text-sm">Auto-posting enabled</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-600 text-white p-2 rounded-lg">📝</div>
                      <div>
                        <h4 className="font-semibold text-green-900">Content Creation</h4>
                        <p className="text-green-700 text-sm">AI-generated content</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-600 text-white p-2 rounded-lg">📊</div>
                      <div>
                        <h4 className="font-semibold text-purple-900">Analytics</h4>
                        <p className="text-purple-700 text-sm">Real-time tracking</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Report */}
                {dailyReport && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">📊 Today's Report</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{dailyReport.totalActivities}</div>
                        <div className="text-sm text-gray-600">Total Activities</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{dailyReport.socialMediaPosts}</div>
                        <div className="text-sm text-gray-600">Social Posts</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{dailyReport.contentGenerated}</div>
                        <div className="text-sm text-gray-600">Content Ideas</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">{dailyReport.emailCampaigns}</div>
                        <div className="text-sm text-gray-600">Email Campaigns</div>
                      </div>
                    </div>
                    
                    {dailyReport.recommendations && (
                      <div>
                        <h4 className="font-semibold mb-2">🎯 AI Recommendations</h4>
                        <ul className="space-y-1">
                          {dailyReport.recommendations.map((rec, index) => (
                            <li key={index} className="text-sm text-gray-700">{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'customers' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-900">👥 Customer Management</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCustomerView('dashboard')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          customerView === 'dashboard' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white text-gray-700 border'
                        }`}
                      >
                        � Dashboard
                      </button>
                      <button
                        onClick={() => setCustomerView('email-setup')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          customerView === 'email-setup' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white text-gray-700 border'
                        }`}
                      >
                        📧 Email Setup
                      </button>
                    </div>
                  </div>
                  
                  {customerView === 'dashboard' && (
                    <>
                      <p className="text-gray-700 mb-4">
                        View and manage all customer inquiries from your AI chatbot. Get real-time notifications when customers provide their details.
                      </p>
                      
                      {/* Customer Stats Quick View */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        {(() => {
                          const stats = getCustomerStats();
                          return (
                            <>
                              <div className="bg-white p-4 rounded-lg shadow text-center">
                                <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                                <div className="text-sm text-gray-600">Total Customers</div>
                              </div>
                              <div className="bg-white p-4 rounded-lg shadow text-center">
                                <div className="text-2xl font-bold text-green-600">{stats.today}</div>
                                <div className="text-sm text-gray-600">Today</div>
                              </div>
                              <div className="bg-white p-4 rounded-lg shadow text-center">
                                <div className="text-2xl font-bold text-orange-600">{stats.new}</div>
                                <div className="text-sm text-gray-600">New Inquiries</div>
                              </div>
                              <div className="bg-white p-4 rounded-lg shadow text-center">
                                <div className="text-2xl font-bold text-purple-600">{stats.thisWeek}</div>
                                <div className="text-sm text-gray-600">This Week</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      
                      <div className="bg-white rounded-lg border p-4">
                        <p className="text-gray-600 mb-4">
                          📧 <strong>Email Notifications:</strong> To receive customer details via email, set up EmailJS using the "Email Setup" tab above.
                        </p>
                        <p className="text-gray-600 mb-2">
                          📱 <strong>WhatsApp Notifications:</strong> Instant notifications are available in the notification panel below.
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Conditional Content */}
                {customerView === 'dashboard' && (
                  <div className="bg-white rounded-lg border">
                    <CustomerDashboard />
                  </div>
                )}
                
                {customerView === 'email-setup' && (
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-xl font-bold mb-4">📧 Email Setup Guide</h3>
                    <p className="text-gray-700 mb-4">
                      Email notifications are temporarily disabled for deployment stability. 
                      Customer data is still being saved locally and can be accessed through the Customer Dashboard.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Current Status:</h4>
                      <ul className="text-blue-800 space-y-1">
                        <li>✅ Customer data capture working</li>
                        <li>✅ Local storage and management working</li>
                        <li>✅ WhatsApp notifications available</li>
                        <li>⏳ Email notifications temporarily disabled</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activities' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">📝 Generated Content & Activities</h3>
                
                {/* AI Generated Content Section */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border">
                  <h4 className="font-semibold text-blue-900 mb-4">🤖 Today's AI-Generated Content</h4>
                  
                  {/* Social Media Posts */}
                  <div className="mb-6">
                    <h5 className="font-medium text-gray-800 mb-3">📱 Social Media Posts (Ready to Copy & Paste)</h5>
                    <div className="space-y-3">
                      
                      {/* Facebook Post */}
                      <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-blue-600">📘 Facebook Post</span>
                          <button 
                            onClick={() => navigator.clipboard.writeText("🏛️ Transform your space with authentic Makrana marble - the same stone used in the Taj Mahal! Our master craftsmen have perfected the art of marble carving for generations. Contact us for premium quality marble at competitive prices. #MakranaMarble #AuthenticMarble #TajMahalStone #PremiumMarble")}
                            className="bg-blue-100 text-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-200"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 text-sm mb-2">
                          "🏛️ Transform your space with authentic Makrana marble - the same stone used in the Taj Mahal! Our master craftsmen have perfected the art of marble carving for generations. Contact us for premium quality marble at competitive prices. #MakranaMarble #AuthenticMarble #TajMahalStone #PremiumMarble"
                        </p>
                        <div className="text-xs text-gray-500">
                          💡 Best posting time: 9:00 AM | Add your marble photos before posting
                        </div>
                      </div>

                      {/* Instagram Post */}
                      <div className="bg-white rounded-lg p-4 border-l-4 border-pink-500">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-pink-600">📸 Instagram Post</span>
                          <button 
                            onClick={() => navigator.clipboard.writeText("✨ Behind the scenes: Watch our master craftsman transform raw Makrana marble into art! Each piece tells a story of tradition and excellence. 🎯 DM us for custom orders! #MakranaMarble #MarbleCraftsmanship #HandCrafted #LuxuryStone")}
                            className="bg-pink-100 text-pink-600 px-3 py-1 rounded text-sm hover:bg-pink-200"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 text-sm mb-2">
                          "✨ Behind the scenes: Watch our master craftsman transform raw Makrana marble into art! Each piece tells a story of tradition and excellence. 🎯 DM us for custom orders! #MakranaMarble #MarbleCraftsmanship #HandCrafted #LuxuryStone"
                        </p>
                        <div className="text-xs text-gray-500">
                          💡 Best posting time: 7:00 PM | Add video/photo of craftsman working
                        </div>
                      </div>

                      {/* LinkedIn Post */}
                      <div className="bg-white rounded-lg p-4 border-l-4 border-blue-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-blue-700">💼 LinkedIn Post</span>
                          <button 
                            onClick={() => navigator.clipboard.writeText("🏗️ Architects & Designers: Discover why Makrana marble remains the gold standard for luxury construction projects. With its unmatched durability and timeless beauty, it's the preferred choice for premium developments worldwide. Connect with us for bulk orders and project consultations.")}
                            className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 text-sm mb-2">
                          "🏗️ Architects & Designers: Discover why Makrana marble remains the gold standard for luxury construction projects. With its unmatched durability and timeless beauty, it's the preferred choice for premium developments worldwide. Connect with us for bulk orders and project consultations."
                        </p>
                        <div className="text-xs text-gray-500">
                          💡 Best posting time: 10:00 AM | Add professional marble architecture photos
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email Templates */}
                  <div className="mb-6">
                    <h5 className="font-medium text-gray-800 mb-3">📧 Email Campaign Templates</h5>
                    <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-green-600">📧 Email to Architects & Designers</span>
                        <button 
                          onClick={() => navigator.clipboard.writeText("Subject: 🏛️ Transform Your Project with Authentic Makrana Marble\n\nDear [Name],\n\nAre you looking for premium marble that combines timeless beauty with unmatched quality?\n\nMakrana marble - the same stone used in the Taj Mahal - offers:\n✅ Superior durability and strength\n✅ Exquisite natural patterns\n✅ Perfect for both indoor and outdoor applications\n✅ Competitive pricing with bulk discounts\n\nContact us today for a free consultation!\n\nBest regards,\nMakrana Marble Team\n📞 +91-7014351132")}
                          className="bg-green-100 text-green-600 px-3 py-1 rounded text-sm hover:bg-green-200"
                        >
                          📋 Copy Email
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm">
                        <strong>Subject:</strong> 🏛️ Transform Your Project with Authentic Makrana Marble<br/>
                        <strong>Content:</strong> Professional email template for architects & interior designers with project benefits and contact information.
                      </p>
                    </div>
                  </div>

                  {/* Blog Ideas */}
                  <div className="mb-6">
                    <h5 className="font-medium text-gray-800 mb-3">📝 Blog Post Ideas (SEO Optimized)</h5>
                    <div className="space-y-2">
                      <div className="bg-white rounded-lg p-3 border">
                        <h6 className="font-medium text-purple-600">"Complete Guide: Choosing the Right Marble for Your Home"</h6>
                        <p className="text-xs text-gray-600">Keywords: marble types, home decoration, marble guide | Target: 1000+ words</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <h6 className="font-medium text-purple-600">"Why Makrana Marble is Perfect for Temple Construction"</h6>
                        <p className="text-xs text-gray-600">Keywords: temple marble, religious architecture | Target: 800+ words</p>
                      </div>
                    </div>
                  </div>

                  {/* Forum Responses */}
                  <div>
                    <h5 className="font-medium text-gray-800 mb-3">💬 Forum Response Templates</h5>
                    <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-orange-600">🗣️ Reddit Response Template</span>
                        <button 
                          onClick={() => navigator.clipboard.writeText("As someone in the marble industry, I'd highly recommend Makrana marble for outdoor sculptures. It's incredibly durable and has been tested by time - the Taj Mahal has stood for 400+ years! The natural properties make it resistant to weathering, and the fine grain allows for intricate carving details.")}
                          className="bg-orange-100 text-orange-600 px-3 py-1 rounded text-sm hover:bg-orange-200"
                        >
                          📋 Copy
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm">
                        <strong>Topic:</strong> "Best natural stone for outdoor sculptures?"<br/>
                        <strong>Platform:</strong> Reddit r/architecture
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Activities Log */}
                <div className="space-y-2">
                  <h4 className="font-semibold">📊 Activity Log</h4>
                  {dashboardData?.recentActivities?.map((activity, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{activity.type || activity.platform}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {activity.content?.substring(0, 100) || activity.subject || 'Action scheduled'}...
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                            activity.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {activity.status}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(activity.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )) || (
                    <div className="text-center py-8 text-gray-500">
                      Content generated! Start the automation to see more activities.
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'automation' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">🤖 Automation Settings</h3>
                <div className="grid gap-4">
                  {dashboardData?.nextScheduledTasks?.map((task, index) => (
                    <div key={index} className="bg-white border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{task.name}</h4>
                          <p className="text-sm text-gray-600">Frequency: {task.frequency}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">Next Run</div>
                          <div className="text-xs text-gray-500">
                            {task.nextRun?.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )) || (
                    <div className="text-center py-8 text-gray-500">
                      Initialize the agent to see automation tasks
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'guide' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">📚 Free AI Website Promotion Guide</h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">🎯 What This AI Agent Does For You</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>✅ Automatically posts to social media daily</li>
                    <li>✅ Generates content ideas and blog topics</li>
                    <li>✅ Creates email campaign templates</li>
                    <li>✅ Suggests forum engagement opportunities</li>
                    <li>✅ Tracks all promotion activities</li>
                    <li>✅ Provides AI-powered recommendations</li>
                  </ul>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">🚀 Manual Actions Required</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>📱 Copy generated content and post to your social accounts</li>
                    <li>📧 Send the prepared email campaigns to your contact list</li>
                    <li>💬 Engage in forums using suggested topics and responses</li>
                    <li>📂 Submit your business to recommended directories</li>
                    <li>📝 Create blog posts from AI-generated ideas</li>
                  </ul>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">🔥 Pro Tips for Maximum Results</h4>
                  <ul className="text-purple-800 text-sm space-y-1">
                    <li>📸 Add high-quality images to all social media posts</li>
                    <li>🎯 Use local hashtags: #MakranaMarble #RajasthanMarble #IndianMarble</li>
                    <li>⏰ Post during peak hours: 9-11 AM and 7-9 PM</li>
                    <li>🤝 Engage with comments and messages promptly</li>
                    <li>📊 Track which content performs best and create more</li>
                    <li>🔗 Always include your website link in descriptions</li>
                  </ul>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-2">📈 Expected Results Timeline</h4>
                  <div className="text-orange-800 text-sm space-y-2">
                    <div><strong>Week 1-2:</strong> Setup complete, content library created</div>
                    <div><strong>Week 3-4:</strong> Increased social media engagement</div>
                    <div><strong>Month 2:</strong> More website visitors from organic sources</div>
                    <div><strong>Month 3+:</strong> Regular inquiries and business growth</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t px-6 py-4">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>🌐 Website: {marketingAgent?.config?.websiteUrl}</div>
              <div>📞 Contact: +91-7014351132</div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIMarketingDashboard;